import { useMemo, useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';

export interface NetworkHealthData {
  nodeId: string;
  timestamp: Date;
  metrics: {
    rtt: number;
    latency: number;
    bandwidth: number;
    packetLoss: number;
    jitter: number;
    connectionQuality: number;
  };
  connectionState: 'connecting' | 'connected' | 'reconnecting' | 'failed' | 'idle';
  errors: number;
  region?: string;
}

export interface LatencyHeatmapData {
  sourceRegion: string;
  targetRegion: string;
  avgLatency: number;
  p95Latency: number;
  p99Latency: number;
  samples: number;
  timestamp: Date;
}

export interface BandwidthFlowData {
  source: string;
  target: string;
  inbound: number;
  outbound: number;
  total: number;
  direction: 'in' | 'out' | 'bidirectional';
  protocol: 'tcp' | 'udp' | 'websocket';
}

interface NetworkHealthMonitorProps {
  healthData: NetworkHealthData[];
  latencyHeatmap: LatencyHeatmapData[];
  bandwidthFlows: BandwidthFlowData[];
  selectedNode?: string;
  timeRange: {
    start: Date;
    end: Date;
  };
  onNodeSelect: (nodeId: string) => void;
  refreshInterval?: number;
}

/**
 * Real-time connection state visualization with animated indicators
 */
function ConnectionStateGrid({ 
  healthData, 
  onNodeSelect, 
  selectedNode, 
}: {
  healthData: NetworkHealthData[];
  onNodeSelect: (nodeId: string) => void;
  selectedNode?: string;
}) {
  const connectionStates = useMemo(() => {
    const states = new Map<string, NetworkHealthData>();
    
    // Get latest state for each node
    healthData.forEach(data => {
      const existing = states.get(data.nodeId);
      if (!existing || data.timestamp > existing.timestamp) {
        states.set(data.nodeId, data);
      }
    });
    
    return Array.from(states.values());
  }, [healthData]);

  const getStateColor = (state: string) => {
    switch (state) {
      case 'connected': return '#10b981';
      case 'connecting': return '#f59e0b';
      case 'reconnecting': return '#f97316';
      case 'failed': return '#ef4444';
      case 'idle': return '#6b7280';
      default: return '#374151';
    }
  };

  const getQualityColor = (quality: number) => {
    if (quality >= 0.9) {
return '#10b981';
}
    if (quality >= 0.7) {
return '#84cc16';
}
    if (quality >= 0.5) {
return '#f59e0b';
}
    if (quality >= 0.3) {
return '#f97316';
}
    return '#ef4444';
  };

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Connection States</h3>
      
      <div className="grid grid-cols-8 gap-2 max-h-96 overflow-y-auto">
        {connectionStates.map((data) => (
          <motion.div
            key={data.nodeId}
            className={`relative p-3 rounded-lg cursor-pointer transition-all ${
              selectedNode === data.nodeId 
                ? 'bg-blue-500/20 ring-2 ring-blue-400' 
                : 'bg-gray-800 hover:bg-gray-700'
            }`}
            role="button"
            tabIndex={0}
            onClick={() => onNodeSelect(data.nodeId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNodeSelect(data.nodeId);
              }
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {/* Connection state indicator */}
            <div 
              className="w-4 h-4 rounded-full mb-2 mx-auto"
              style={{ backgroundColor: getStateColor(data.connectionState) }}
            >
              {(data.connectionState === 'connecting' || data.connectionState === 'reconnecting') && (
                <motion.div
                  className="w-full h-full rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
              )}
            </div>

            {/* Node ID */}
            <div className="text-xs text-center text-gray-300 mb-1 truncate">
              {data.nodeId.slice(-8)}
            </div>

            {/* Connection quality bar */}
            <div className="w-full bg-gray-700 rounded-full h-1.5 mb-2">
              <motion.div
                className="h-1.5 rounded-full transition-all duration-500"
                style={{ 
                  backgroundColor: getQualityColor(data.metrics.connectionQuality),
                  width: `${data.metrics.connectionQuality * 100}%`,
                }}
                initial={{ width: 0 }}
                animate={{ width: `${data.metrics.connectionQuality * 100}%` }}
              />
            </div>

            {/* RTT indicator */}
            <div className="text-xs text-center">
              <span className="text-primary-400">{data.metrics.rtt.toFixed(0)}ms</span>
            </div>

            {/* Error indicator */}
            {data.errors > 0 && (
              <div className="mt-1 text-center">
                <motion.span
                  className="inline-flex items-center justify-center bg-danger-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px]"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  {data.errors > 99 ? '99+' : data.errors}
                </motion.span>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-xs">
        {[
          { state: 'connected', label: 'Connected' },
          { state: 'connecting', label: 'Connecting' },
          { state: 'reconnecting', label: 'Reconnecting' },
          { state: 'failed', label: 'Failed' },
          { state: 'idle', label: 'Idle' },
        ].map(({ state, label }) => (
          <div key={state} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getStateColor(state) }}
            />
            <span className="text-gray-400">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Latency heatmap visualization between regions
 */
function LatencyHeatmap({ 
  latencyData, 
}: {
  latencyData: LatencyHeatmapData[];
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredCell, setHoveredCell] = useState<LatencyHeatmapData | null>(null);

  const { regions, heatmapMatrix, maxLatency } = useMemo(() => {
    const regionSet = new Set<string>();
    latencyData.forEach(d => {
      regionSet.add(d.sourceRegion);
      regionSet.add(d.targetRegion);
    });
    
    const regions = Array.from(regionSet).sort();
    const matrix: LatencyHeatmapData[][] = [];
    let maxLatency = 0;

    // Create matrix
    regions.forEach((sourceRegion, i) => {
      matrix[i] = [];
      regions.forEach((targetRegion, j) => {
        const data = latencyData.find(d => 
          d.sourceRegion === sourceRegion && d.targetRegion === targetRegion,
        );
        matrix[i][j] = data || {
          sourceRegion,
          targetRegion,
          avgLatency: 0,
          p95Latency: 0,
          p99Latency: 0,
          samples: 0,
          timestamp: new Date(),
        };
        
        if (data && data.avgLatency > maxLatency) {
          maxLatency = data.avgLatency;
        }
      });
    });

    return { regions, heatmapMatrix: matrix, maxLatency };
  }, [latencyData]);

  const colorScale = useMemo(() => {
    return d3.scaleSequential(d3.interpolateRdYlGn)
      .domain([maxLatency, 0]); // Reverse scale so green = low latency
  }, [maxLatency]);

  useEffect(() => {
    if (!svgRef.current || regions.length === 0) {
return;
}

    const svg = d3.select(svgRef.current);
    const margin = { top: 40, right: 40, bottom: 40, left: 80 };
    const width = 400 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;
    
    const cellSize = Math.min(width / regions.length, height / regions.length);

    svg.selectAll('*').remove();

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create cells
    heatmapMatrix.forEach((row, i) => {
      row.forEach((cell, j) => {
        g.append('rect')
          .attr('x', j * cellSize)
          .attr('y', i * cellSize)
          .attr('width', cellSize)
          .attr('height', cellSize)
          .attr('fill', cell.avgLatency > 0 ? colorScale(cell.avgLatency) : '#374151')
          .attr('stroke', '#1f2937')
          .attr('stroke-width', 1)
          .style('cursor', 'pointer')
          .on('mouseover', (_event) => {
            setHoveredCell(cell);
          })
          .on('mouseout', () => {
            setHoveredCell(null);
          });

        // Add latency text
        if (cell.avgLatency > 0) {
          g.append('text')
            .attr('x', j * cellSize + cellSize / 2)
            .attr('y', i * cellSize + cellSize / 2)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('fill', cell.avgLatency > maxLatency / 2 ? 'white' : 'black')
            .attr('font-size', '10px')
            .text(`${cell.avgLatency.toFixed(0)}ms`);
        }
      });
    });

    // Add row labels (source regions)
    g.selectAll('.row-label')
      .data(regions)
      .enter()
      .append('text')
      .attr('class', 'row-label')
      .attr('x', -10)
      .attr('y', (_d, i) => i * cellSize + cellSize / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#9ca3af')
      .attr('font-size', '11px')
      .text(d => d);

    // Add column labels (target regions)
    g.selectAll('.col-label')
      .data(regions)
      .enter()
      .append('text')
      .attr('class', 'col-label')
      .attr('x', (_d, i) => i * cellSize + cellSize / 2)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#9ca3af')
      .attr('font-size', '11px')
      .text(d => d);

  }, [regions, heatmapMatrix, colorScale, maxLatency]);

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Regional Latency Heatmap</h3>
      
      <div className="relative">
        <svg
          ref={svgRef}
          width="480"
          height="400"
          className="bg-gray-800 rounded"
        />
        
        {/* Tooltip */}
        <AnimatePresence>
          {hoveredCell && hoveredCell.avgLatency > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute bottom-4 left-4 bg-black/90 rounded-lg p-3 text-white text-sm cursor-pointer"
              onClick={() => setHoveredCell(null)}
            >
              <div className="font-semibold">
                {hoveredCell.sourceRegion} → {hoveredCell.targetRegion}
              </div>
              <div className="space-y-1 mt-2">
                <div>Avg: <span className="text-primary-400">{hoveredCell.avgLatency.toFixed(1)}ms</span></div>
                <div>P95: <span className="text-yellow-400">{hoveredCell.p95Latency.toFixed(1)}ms</span></div>
                <div>P99: <span className="text-danger-400">{hoveredCell.p99Latency.toFixed(1)}ms</span></div>
                <div>Samples: <span className="text-status-online">{hoveredCell.samples}</span></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/**
 * Real-time bandwidth flow visualization
 */
function BandwidthFlowVisualization({ 
  bandwidthFlows,
  onNodeSelect: _onNodeSelect, 
}: {
  bandwidthFlows: BandwidthFlowData[];
  onNodeSelect: (nodeId: string) => void;
}) {
  const maxBandwidth = useMemo(() => {
    return Math.max(...bandwidthFlows.map(f => f.total));
  }, [bandwidthFlows]);

  const formatBandwidth = (bytes: number) => {
    if (bytes >= 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB/s`;
    } else if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB/s`;
    } else if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(1)} KB/s`;
    }
    return `${bytes} B/s`;
  };

  const getProtocolColor = (protocol: string) => {
    switch (protocol) {
      case 'tcp': return '#06b6d4';
      case 'udp': return '#10b981';
      case 'websocket': return '#9333ea';
      default: return '#6b7280';
    }
  };

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Bandwidth Flows</h3>
      
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {bandwidthFlows
          .sort((a, b) => b.total - a.total)
          .map((flow, index) => (
            <motion.div
              key={`${flow.source}-${flow.target}-${index}`}
              className="bg-gray-800 rounded-lg p-3 hover:bg-gray-700 transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span 
                    className="px-2 py-1 rounded text-xs text-white"
                    style={{ backgroundColor: getProtocolColor(flow.protocol) }}
                  >
                    {flow.protocol.toUpperCase()}
                  </span>
                  <span className="text-gray-300 text-sm">
                    {flow.source} → {flow.target}
                  </span>
                </div>
                <span className="text-primary-400 font-mono text-sm">
                  {formatBandwidth(flow.total)}
                </span>
              </div>

              {/* Bandwidth visualization bars */}
              <div className="space-y-1">
                {/* Inbound */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-12">In:</span>
                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                    <motion.div
                      className="bg-status-online h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${(flow.inbound / maxBandwidth) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <span className="text-xs text-status-online w-16">
                    {formatBandwidth(flow.inbound)}
                  </span>
                </div>

                {/* Outbound */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-12">Out:</span>
                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                    <motion.div
                      className="bg-primary-500 h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${(flow.outbound / maxBandwidth) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <span className="text-xs text-primary-400 w-16">
                    {formatBandwidth(flow.outbound)}
                  </span>
                </div>
              </div>

              {/* Flow direction indicator */}
              <div className="flex justify-center mt-2">
                <div className="flex items-center gap-1">
                  {flow.direction === 'bidirectional' ? (
                    <>
                      <div className="w-3 h-0.5 bg-primary-400" />
                      <div className="w-0 h-0 border-l-2 border-l-primary-400 border-t border-t-transparent border-b border-b-transparent" />
                      <div className="w-0 h-0 border-r-2 border-r-primary-400 border-t border-t-transparent border-b border-b-transparent" />
                      <div className="w-3 h-0.5 bg-primary-400" />
                    </>
                  ) : (
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-primary-400" />
                      <div className="w-0 h-0 border-l-2 border-l-primary-400 border-t border-t-transparent border-b border-b-transparent" />
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        }
      </div>
    </div>
  );
}

/**
 * Network congestion detection and alerts
 */
function NetworkCongestionAlerts({ 
  healthData, 
}: {
  healthData: NetworkHealthData[];
}) {
  const congestionAlerts = useMemo(() => {
    const alerts: Array<{
      nodeId: string;
      severity: 'low' | 'medium' | 'high' | 'critical';
      type: 'latency' | 'packet_loss' | 'jitter' | 'bandwidth';
      value: number;
      threshold: number;
    }> = [];

    healthData.forEach(data => {
      // High latency alert
      if (data.metrics.latency > 200) {
        alerts.push({
          nodeId: data.nodeId,
          severity: data.metrics.latency > 500 ? 'critical' : 'high',
          type: 'latency',
          value: data.metrics.latency,
          threshold: 200,
        });
      }

      // Packet loss alert
      if (data.metrics.packetLoss > 0.01) {
        alerts.push({
          nodeId: data.nodeId,
          severity: data.metrics.packetLoss > 0.05 ? 'critical' : 'medium',
          type: 'packet_loss',
          value: data.metrics.packetLoss * 100,
          threshold: 1,
        });
      }

      // High jitter alert
      if (data.metrics.jitter > 50) {
        alerts.push({
          nodeId: data.nodeId,
          severity: data.metrics.jitter > 100 ? 'high' : 'medium',
          type: 'jitter',
          value: data.metrics.jitter,
          threshold: 50,
        });
      }
    });

    return alerts.sort((a, b) => {
      const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      return severityOrder[b.severity] - severityOrder[a.severity];
    });
  }, [healthData]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#d97706';
      case 'low': return '#65a30d';
      default: return '#6b7280';
    }
  };

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Network Congestion Alerts</h3>
      
      {congestionAlerts.length === 0 ? (
        <div className="text-center text-status-online py-8">
          <div className="text-2xl mb-2">✓</div>
          <div>No congestion detected</div>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {congestionAlerts.map((alert, index) => (
            <motion.div
              key={`${alert.nodeId}-${alert.type}`}
              className="bg-gray-800 rounded-lg p-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-white font-medium">{alert.nodeId.slice(-8)}</span>
                  <span className="text-gray-400 text-sm ml-2">
                    {alert.type.replace('_', ' ')}
                  </span>
                </div>
                <span 
                  className="px-2 py-1 rounded text-xs text-white"
                  style={{ backgroundColor: getSeverityColor(alert.severity) }}
                >
                  {alert.severity.toUpperCase()}
                </span>
              </div>
              
              <div className="mt-1 text-sm text-gray-300">
                Current: <span className="text-danger-400">{alert.value.toFixed(1)}</span>
                {alert.type === 'packet_loss' ? '%' : 'ms'} | 
                Threshold: <span className="text-yellow-400">{alert.threshold}</span>
                {alert.type === 'packet_loss' ? '%' : 'ms'}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Main network health monitoring dashboard
 */
export function NetworkHealthMonitor({
  healthData,
  latencyHeatmap,
  bandwidthFlows,
  selectedNode,
  timeRange,
  onNodeSelect,
  refreshInterval = 5000,
}: NetworkHealthMonitorProps) {
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Auto-refresh data
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  // Filter data by time range
  const filteredHealthData = useMemo(() => {
    return healthData.filter(data => 
      data.timestamp >= timeRange.start && data.timestamp <= timeRange.end,
    );
  }, [healthData, timeRange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Network Health Monitor</h2>
        <div className="text-sm text-gray-400">
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      </div>

      {/* Top row - Connection states and congestion alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ConnectionStateGrid
          healthData={filteredHealthData}
          onNodeSelect={onNodeSelect}
          selectedNode={selectedNode}
        />
        <NetworkCongestionAlerts healthData={filteredHealthData} />
      </div>

      {/* Middle row - Latency heatmap and bandwidth flows */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <LatencyHeatmap latencyData={latencyHeatmap} />
        <BandwidthFlowVisualization 
          bandwidthFlows={bandwidthFlows}
          onNodeSelect={onNodeSelect}
        />
      </div>
    </div>
  );
}

export default NetworkHealthMonitor;